import React, { useState } from 'react';
import { BadgeCheck, UserCircle2 } from 'lucide-react';
import api from '@/lib/axios';

const OPCOES = [
  ['primeiro', 'Apenas primeiro nome'],
  ['primeiro_inicial', 'Primeiro nome e inicial do apelido'],
  ['alcunha', 'Nome público ou alcunha'],
];

// Bloco B da Fase 4: escolha do nome público (o nome declarado nunca é público).
export default function PublicNameCard({ listingId, identidade, declarado, email, token, onSaved }) {
  const [estilo, setEstilo] = useState(identidade?.estilo || 'primeiro');
  const [alcunha, setAlcunha] = useState(identidade?.alcunha || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const preview = () => {
    const primeiro = (declarado?.primeiro_nome || '').trim();
    const ultimo = (declarado?.ultimo_nome || '').trim();
    if (estilo === 'alcunha') return alcunha.trim() || '—';
    if (estilo === 'primeiro_inicial') return ultimo ? `${primeiro} ${ultimo[0].toUpperCase()}.` : primeiro;
    return primeiro || '—';
  };

  const guardar = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await api.post(`/marketplace/self/listings/${listingId}/nome-publico`, {
        email, verification_token: token, estilo, alcunha,
      });
      setMsg(`Nome público atualizado: ${res.data.identidade.nome_publico}`);
      onSaved?.(res.data.identidade);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Não foi possível guardar o nome público.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`public-name-card-${listingId}`}>
      <p className="inline-flex items-center gap-2 text-xs font-bold text-slate-700">
        <UserCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Nome mostrado no anúncio
      </p>
      <p className="mt-1 text-[11px] text-slate-500">
        Nas páginas públicas mostramos apenas o nome público. O nome completo declarado só aparece dentro das conversas.
      </p>
      <div className="mt-2 grid gap-2">
        {OPCOES.map(([valor, label]) => (
          <label key={valor} className="flex items-center gap-2 text-xs text-slate-700" data-testid={`public-name-option-${valor}-${listingId}`}>
            <input type="radio" name={`nomepub-${listingId}`} checked={estilo === valor}
              onChange={() => setEstilo(valor)} data-testid={`public-name-radio-${valor}-${listingId}`} />
            {label}
          </label>
        ))}
        {estilo === 'alcunha' && (
          <input value={alcunha} onChange={(e) => setAlcunha(e.target.value)} maxLength={40}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" placeholder="Ex.: Tiago do Porto"
            data-testid={`public-name-alias-${listingId}`} />
        )}
      </div>
      <p className="mt-2 text-xs text-slate-600">
        Ficará visível como <strong data-testid={`public-name-preview-${listingId}`}>{preview()}</strong>
      </p>
      <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-700">
        <BadgeCheck className="h-3 w-3" /> Email verificado — o Marketplace confirma contactos, não documentos de identidade.
      </p>
      {identidade?.pedido_alteracao && (
        <p className="mt-2 rounded-lg bg-amber-50 p-2 text-[11px] font-semibold text-amber-800" data-testid={`public-name-request-${listingId}`}>
          {identidade.pedido_alteracao}
        </p>
      )}
      <button type="button" onClick={guardar} disabled={busy}
        className="mt-2 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60"
        data-testid={`public-name-save-${listingId}`}>{busy ? 'A guardar...' : 'Guardar nome público'}</button>
      {msg && <p className="mt-2 text-[11px] font-semibold text-emerald-700" data-testid={`public-name-ok-${listingId}`}>{msg}</p>}
      {err && <p className="mt-2 text-[11px] font-semibold text-red-600" data-testid={`public-name-error-${listingId}`}>{err}</p>}
    </div>
  );
}
